/**
 * The ledger's rows, exactly as they leave the database.
 *
 * One set of types for both sides (ADR-0025). The `_v` views already render
 * uuids as strings and leave everything else alone, and the replica ships
 * those rows through untouched — so what `services/api` reads with a `SELECT`
 * and what the client reads out of its `Dataset` are the same objects, and a
 * function over them is a function both can call.
 *
 * Decimals stay strings, as MySQL hands them over. Turning them into numbers
 * here would put a float in the one place this app refuses to have one, and
 * every caller that needs arithmetic goes through `toScaled`/`fromScaled`.
 */

export interface AccountRow {
  id: string;
  parentId: string | null;
  name: string;
  type: string;
  currencyCode: string;
  isPlaceholder?: number | boolean | null;
  isMulticurrency?: number | boolean | null;
  sortOrder?: number | null;
  description?: string | null;
  iconPath?: string | null;
}

export interface TransactionRow {
  id: string;
  txDate: string;
  description?: string | null;
  reference?: string | null;
  currencyCode: string;
  seq?: number | null;
  version?: number;
  /**
   * The transaction's legs, on the transaction (ADR-0026).
   *
   * Optional while the move is under way: a row read from a device that has
   * not re-synced since, or from a path that does not select the column, has
   * none — and `splitsOf` treats that as "ask the splits table" rather than
   * as "this transaction has no legs", which would silently unbalance it.
   */
  legs?: readonly TransactionLeg[] | null;
}

/**
 * One leg as it is stored on its transaction.
 *
 * A `SplitRow` without `transactionId`, because the row it sits on already
 * says which transaction it belongs to, and without `version`, because a leg
 * is versioned with the document it is part of rather than on its own.
 */
export interface TransactionLeg {
  id: string;
  accountId: string;
  memo?: string | null;
  value: string;
  amount: string;
  currencyCode: string;
  walletId: string | null;
}

export interface SplitRow {
  id: string;
  transactionId: string;
  accountId: string;
  memo?: string | null;
  /** In the TRANSACTION's currency — what must sum to zero. */
  value: string;
  /** In the ACCOUNT's currency — what the register shows. */
  amount: string;
  currencyCode: string;
  walletId: string | null;
  version?: number;
  /**
   * Set only on a leg that is not in the entry: the earmark move a debt
   * implies when borrowed money left the BORROWER's own wallet
   * (`debtShiftLegs`). Named by the transfer it comes from. Never written,
   * never shown in an editor — only the wallet views read these.
   */
  shiftOf?: string;
}

export interface WalletRow {
  id: string;
  name: string;
  /** What to call it where there is no room for the name. Optional; falls back to `name`. */
  shortName?: string | null;
  /** `#rrggbb`, or nothing — see `walletColours` for what is drawn when it is nothing. */
  color?: string | null;
  parentId: string | null;
  sortOrder?: number | null;
}

export interface WalletTransferRow {
  id: string;
  transactionId: string;
  kind: string;
  fromWalletId: string;
  toWalletId: string;
  /** The leg this transfer moved, and so how much it was. Absent on rows written before it existed. */
  amountSplitId?: string | null;
  /** How much of that leg is owed, when not all of it. Absent or null: the whole leg. */
  amount?: string | null;
}

export interface TagRow {
  id: string;
  name: string;
  sortOrder?: number | null;
}

export interface TransactionTagRow {
  id: string;
  transactionId: string;
  tagId: string;
}

export interface BudgetRow {
  id: string;
  tagId: string;
  walletId: string | null;
  currencyCode: string;
  amount: string;
  fromDate: string | null;
  toDate: string | null;
  note: string | null;
  /** Put away when the project is over — see the manifest. */
  hidden?: boolean | null;
  version?: number;
}

export interface CurrencyRow {
  id: string;
  code: string;
  name: string;
  symbol?: string | null;
  fraction?: number | null;
  useForTotals?: number | boolean | null;
  /** Set by dragging on the Settings page; see `currencyRank`. */
  sortOrder?: number | null;
}

export interface AttachmentRow {
  id: string;
  transactionId: string | null;
  originalName: string;
  mime: string;
  /** Opaque handle into the on-disk store. Never a path the client should build a URL from. */
  storageKey: string;
  size?: number | null;
  /**
   * A data URI, small enough to travel with the row. The FILE does not
   * replicate — see ADR-0025: a receipt is megabytes and a ledger has
   * thousands of them, so the thumbnail is what a device holds and opening
   * the original needs a connection.
   */
  thumb?: string | null;
}

export interface TransactionBankRefRow {
  id: string;
  transactionId: string;
  externalSrc: string;
  externalRef: string;
  bankAccountId: string | null;
  /** The provider's JSON for this entry, as text. ~200 bytes in practice. */
  bankRaw: string | null;
}

export interface BankAccountRow {
  id: string;
  connectionId: string;
  iban: string | null;
  name: string | null;
  currency: string | null;
}

export interface BankConnectionRow {
  id: string;
  aspspName: string;
  aspspCountry: string;
}

export interface AccountPurposeRow {
  id: string;
  accountId: string;
  purposeId: string;
}

export interface PurposeRow {
  id: string;
  name: string;
  sortOrder?: number | null;
  iconPath?: string | null;
}

/**
 * Everything the ledger's questions are asked of.
 *
 * Deliberately an interface over *getters* rather than a bag of arrays: the
 * client answers each from an in-memory mirror it already holds, and the
 * server from rows it loaded for this request, and neither should have to
 * materialise what a given question does not ask for.
 *
 * `services/api` and the client each implement this once. Everything in
 * `apps/webledger/domain/` is written against it and therefore runs in both
 * places — which is the point: one implementation of what a filter means,
 * rather than two that can disagree.
 */
export interface Ledger {
  accounts(): readonly AccountRow[];
  transactions(): readonly TransactionRow[];
  splits(): readonly SplitRow[];
  wallets(): readonly WalletRow[];
  walletTransfers(): readonly WalletTransferRow[];
  tags(): readonly TagRow[];
  transactionTags(): readonly TransactionTagRow[];
  budgets(): readonly BudgetRow[];
  currencies(): readonly CurrencyRow[];
  attachments(): readonly AttachmentRow[];
  purposes(): readonly PurposeRow[];
  accountPurposes(): readonly AccountPurposeRow[];
  transactionBankRefs(): readonly TransactionBankRefRow[];
  bankAccounts(): readonly BankAccountRow[];
  bankConnections(): readonly BankConnectionRow[];
}

/** An index by id, which nearly every question here wants. */
export function byId<T extends { id: string }>(rows: readonly T[]): Map<string, T> {
  return new Map(rows.map((r) => [r.id, r]));
}

/** Group rows by a key, preserving input order within each group. */
export function groupBy<T>(rows: readonly T[], key: (row: T) => string | null): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    if (k === null) continue;
    const list = out.get(k);
    if (list) list.push(row); else out.set(k, [row]);
  }
  return out;
}

/** MySQL hands booleans back as 0/1; the client's JSON keeps whatever it was told. */
export function isTrue(value: number | boolean | null | undefined): boolean {
  return value === true || value === 1;
}

/**
 * One transaction's attachments, from the replica.
 *
 * The rows carry `thumb` and never the file — see `AttachmentRow.thumb` and
 * ADR-0025. A caller that wants to OPEN one still needs the server, which
 * mints a short-lived signed url; a caller that only wants to show what is
 * attached needs nothing but this.
 */
export function attachmentsOf(ledger: Ledger, transactionId: string): AttachmentRow[] {
  return ledger.attachments().filter((a) => a.transactionId === transactionId);
}

/**
 * One transaction with its legs, as the editors take it.
 *
 * The same shape `loadTransactionsWithSplits` builds server-side, minus the
 * three lock fields — who holds it, since when, and whether they are
 * connected — which are live hold state nobody replicates, and which the caller
 * fills in when it can reach the server (ADR-0025).
 */
export function transactionWithSplits(
  ledger: Ledger, transactionId: string,
): (TransactionRow & { splits: SplitRow[] }) | undefined {
  const tx = ledger.transactions().find((t) => t.id === transactionId);
  if (!tx) return undefined;
  return {
    ...tx,
    description: tx.description ?? '',
    reference: tx.reference ?? '',
    // Ordered by id, matching the server, so the editor's rows do not shuffle
    // between an offline open and an online one.
    splits: ledger.splits()
      .filter((s) => s.transactionId === transactionId)
      .sort((a, b) => a.id.localeCompare(b.id)),
  };
}

/**
 * A ledger's legs, from the transactions that carry them.
 *
 * The one place that knows legs live on the transaction now (ADR-0026).
 * Everything downstream — the register, the wallet views, the reports, the
 * budgets — still reads `Ledger.splits()` and still gets `SplitRow`s, so the
 * move did not have to be made in each of them separately.
 *
 * A transaction whose `legs` is null or absent contributes none. That is the
 * honest reading of a row from a device that has not re-synced, or from a
 * query that did not select the column: it says "unknown", and the caller
 * supplying these decides whether to fall back to the splits table. Treating
 * it as "no legs" here would quietly unbalance the transaction instead.
 */
export function splitsFromTransactions(
  transactions: readonly TransactionRow[],
): SplitRow[] {
  const out: SplitRow[] = [];
  for (const tx of transactions) {
    for (const leg of tx.legs ?? []) out.push({ ...leg, transactionId: tx.id });
  }
  return out;
}

/** Whether any transaction here is still waiting for its legs to arrive. */
export function anyLegsMissing(transactions: readonly TransactionRow[]): boolean {
  return transactions.some((tx) => tx.legs === undefined || tx.legs === null);
}
